<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  useSlots,
  Comment,
  Text,
  Fragment,
  cloneVNode,
  defineAsyncComponent,
  watch
} from 'vue';

const BaseSquaredButton = defineAsyncComponent(() =>
    import(/* webpackChunkName: "BaseSquaredButton" */ '@/components/base/buttons/BaseSquaredButton.vue')
);

const props = defineProps({
  slideWidth: {
    type: Number,
    required: true,
    default: 335 // px
  },
  gap: {
    type: Number,
    default: 20 // px
  },
  defaultCardIndex: {
    type: Number,
    default: 1
  }
});

const emit = defineEmits(['updateIndex']);

const slots = useSlots(); // Доступ к содержимому слотов

const carouselWrapper = ref(null);
const carouselTrack = ref(null);

// --- Настройки клонирования ---
// Количество слайдов, клонируемых с каждой стороны.
const numClonesPerSide = 1;

// Оригинальные VNodes из слота. Их количество - N.
const originalSlidesVNodes = ref([]);

// Массив VNodes для рендеринга (включает клоны): [клоны_конца, оригиналы, клоны_начала]
const renderableSlides = computed(() => {
  if (originalSlidesVNodes.value.length === 0) {
    return [];
  }

  const numOriginal = originalSlidesVNodes.value.length;

  if (numOriginal === 1) {
    return originalSlidesVNodes.value
  }
  // Если меньше слайдов, чем клонов, или всего 1 слайд, клонировать нужно умнее
  // if (numOriginal <= numClonesPerSide) {
  //   // Для случаев, когда слайдов очень мало (например, 1-2)
  //   // Просто дублируем их несколько раз для эффекта
  //   let tempSlides = [...originalSlidesVNodes.value];
  //   while (tempSlides.length < numClonesPerSide * 2 + numOriginal) {
  //     tempSlides = [...tempSlides, ...originalSlidesVNodes.value];
  //   }
  //   return tempSlides.map((v, i) => { v.key = `cloned-fill-${i}-${v.key || ''}`; return cloneVNode(v); });
  // }

  const clonedStart = originalSlidesVNodes.value.slice(0, numClonesPerSide).map(v => cloneVNode(v));
  const clonedEnd = originalSlidesVNodes.value.slice(numOriginal - numClonesPerSide).map(v => cloneVNode(v));

  // Возвращаем: [клоны_конца_оригиналов, оригиналы, клоны_начала_оригиналов]
  const clonedRenderList = [
    ...clonedEnd.map((v, i) => { v.key = `cloned-end-${i}-${v.key || ''}`; return v; }),
    ...originalSlidesVNodes.value,
    ...clonedStart.map((v, i) => { v.key = `cloned-start-${i}-${v.key || ''}`; return v; })
  ];
  return clonedRenderList;
});

// Общее количество слайдов для рендеринга (включая клоны)
// const numRenderableSlides = computed(() => renderableSlides.value.length);

// Количество оригинальных слайдов (для публичного индекса и точек)
const numOriginalSlides = computed(() => originalSlidesVNodes.value.length);
const numOriginalSlidesArray = computed(() => Array.from({ length: numOriginalSlides.value }, (_, i) => i));

// Внутренний индекс: позиция в массиве renderableSlides
// Изначально указывает на первый *оригинальный* слайд (после клонов_конца)
const internalCurrentIndex = ref(props.defaultCardIndex);

// Публичный индекс: индекс видимого *оригинального* слайда (0 до N-1)
const publicCurrentIndex = computed(() => {
  if (numOriginalSlides.value === 0) return 0;
  // Маппинг внутреннего индекса на публичный, учитывая клоны
  let index = internalCurrentIndex.value - numClonesPerSide;
  // Гарантируем, что индекс всегда в диапазоне оригинальных слайдов
  // Модульная арифметика для циклического маппинга
  return (index % numOriginalSlides.value + numOriginalSlides.value) % numOriginalSlides.value;
});

watch(publicCurrentIndex, (v) => {
  emit('updateIndex', v)
})

const slideFullWidth = computed(() => props.slideWidth + props.gap);
const paddingX = computed(() => props.slideWidth * props.sideSliceFraction);


// Флаг для предотвращения множественных быстрых переходов во время анимации
const isAnimating = ref(false);

const debounce = (func, delay) => {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
};

/**
 * Прокручивает карусель к указанному внутреннему индексу.
 * @param {number} targetInternalIndex - Индекс в массиве renderableSlides.
 * @param {boolean} smooth - Использовать ли плавную прокрутку (true/false).
 */
const goToInternalSlide = (targetInternalIndex, smooth = true) => {
  if (!carouselTrack.value || !carouselWrapper.value) {
    return;
  }

  // Блокируем только если пытаемся начать *плавную* анимацию, когда уже идет другая.
  // Мгновенные прыжки (smooth=false) не блокируются, так как они часть цикла завершения.
  if (smooth && isAnimating.value) {
    return;
  }

  carouselTrack.value.style.scrollBehavior = smooth ? 'smooth' : 'auto';

  const wrapperWidth = carouselWrapper.value.offsetWidth;
  const slideCenterInTrack = (targetInternalIndex * slideFullWidth.value) + (props.slideWidth / 2);
  const scrollPosition = slideCenterInTrack - (wrapperWidth / 2);

  if (smooth) {
    isAnimating.value = true; // Устанавливаем флаг только для плавной прокрутки
  } else {
    // Для мгновенных прыжков (teleportations) isAnimating не должен блокировать,
    // и он будет сброшен в _handleScrollEndLogic (после того как весь цикл завершится).
    // Если это самый первый вызов (onMounted), то сбросим сразу.
    if (targetInternalIndex === numClonesPerSide && carouselTrack.value.scrollLeft === 0) { // Проверка для onMounted
      isAnimating.value = false;
    }
  }

  carouselTrack.value.scrollTo({
    left: scrollPosition
  });

  internalCurrentIndex.value = targetInternalIndex;
};

// Метод для обработки завершения прокрутки (для бесшовного прыжка/телепортации)
const debouncedHandleScrollEnd = debounce(() => {
  // Важно: вызываем _handleScrollEndLogic только если это был smooth-скролл,
  // а не мгновенный прыжок, который сам по себе не требует дальнейшей телепортации.
  if (carouselTrack.value && carouselTrack.value.style.scrollBehavior === 'smooth') {
    _handleScrollEndLogic(); // Вызываем основную логику телепортации
  } else {
    // Если сюда попали и scrollBehavior не smooth, значит, это был мгновенный прыжок.
    // В этом случае, возможно, нужно просто сбросить isAnimating, если он еще не сброшен.
    isAnimating.value = false;
  }
}, 50); // Дебаунс, чтобы прокрутка успела стабилизироваться

const _handleScrollEndLogic = () => {
  if (!carouselTrack.value || numOriginalSlides.value === 0) {
    isAnimating.value = false;
    return;
  }

  let needsJump = false;
  let jumpTargetInternalIndex = -1;

  // Если мы оказались на клоне *первого* оригинального слайда (справа)
  if (internalCurrentIndex.value >= numOriginalSlides.value + numClonesPerSide) {
    const offsetInClonesStartBlock = internalCurrentIndex.value - (numOriginalSlides.value + numClonesPerSide);
    jumpTargetInternalIndex = numClonesPerSide + offsetInClonesStartBlock;
    needsJump = true;
  }
  // Если мы оказались на клоне *последнего* оригинального слайда (слева)
  else if (internalCurrentIndex.value < numClonesPerSide) {
    // console.log(`[_handleScrollEndLogic] Обнаружен переход к клону КОНЦА (слева).`);
    const offsetInClonesEndBlock = internalCurrentIndex.value;
    jumpTargetInternalIndex = numClonesPerSide + (numOriginalSlides.value - numClonesPerSide) + offsetInClonesEndBlock;
    needsJump = true;
  }

  if (needsJump) {
    goToInternalSlide(jumpTargetInternalIndex, false); // Мгновенный прыжок
  }

  isAnimating.value = false; // Сбрасываем флаг только после завершения всех потенциальных прыжков
};

// --- Навигационные методы ---
const nextSlide = () => {
  if (isAnimating.value || numOriginalSlides.value === 0) return;

  // Целевой внутренний индекс: следующий по порядку
  let targetInternalIndex = internalCurrentIndex.value + 1;
  goToInternalSlide(targetInternalIndex, true);
};

const prevSlide = () => {
  if (isAnimating.value || numOriginalSlides.value === 0) return;

  // Целевой внутренний индекс: предыдущий по порядку
  let targetInternalIndex = internalCurrentIndex.value - 1;
  goToInternalSlide(targetInternalIndex, true);
};

/**
 * Прокрутка к публичному (оригинальному) слайду по индексу
 * @param {number} publicIndex - Индекс оригинального слайда (0 до N-1)
 */
const goToPublicSlide = (publicIndex) => {
  if (isAnimating.value || publicIndex < 0 || publicIndex >= numOriginalSlides.value) return;

  // Переводим публичный индекс во внутренний, добавляя смещение клонов
  const targetInternalIndex = publicIndex + numClonesPerSide;
  goToInternalSlide(targetInternalIndex, true);
};

// --- Жизненный цикл и слушатели событий ---

const handleResize = () => {
  // При изменении размера окна, пересчитываем позицию и прокручиваем к текущему слайду
  // с плавностью, чтобы он оставался центрированным.
  goToInternalSlide(internalCurrentIndex.value, true);
};

onMounted(() => {
  // Захватываем VNodes из слота. Теперь с правильным доступом к children.
  if (slots.default) {
    const rawVnodes = slots.default();

    let actualVnodes = [];
    // Проверяем, есть ли первый VNode и есть ли у него дети-массив
    if (rawVnodes.length > 0 && rawVnodes[0] && Array.isArray(rawVnodes[0].children)) {
      actualVnodes = rawVnodes[0].children;
    } else if (rawVnodes.length > 0) {
      // Это случай, когда в слоте несколько корневых элементов или один, который не обернут
      actualVnodes = rawVnodes;
      console.log('Carousel onMounted: Using rawVnodes directly (no children property):', actualVnodes);
    } else {
      console.log('Carousel onMounted: rawVnodes is empty or invalid.');
    }

    // Фильтруем полученные VNodes
    const filteredVnodes = actualVnodes.filter(
        (vnode) => vnode.type !== Comment && vnode.type !== Text && vnode.type !== Fragment
    );

    originalSlidesVNodes.value = filteredVnodes;

    // Инициализация карусели
    if (originalSlidesVNodes.value.length === 0) {
      return;
    }

    setTimeout(() => {
      if (carouselTrack.value && carouselTrack.value.scrollWidth > 0) {
        goToInternalSlide(internalCurrentIndex.value, false);

        if (carouselTrack.value) {
          carouselTrack.value.addEventListener('scrollend', debouncedHandleScrollEnd);
          carouselTrack.value.addEventListener('transitionend', debouncedHandleScrollEnd);
        }
        window.addEventListener('resize', handleResize);
      }
    }, 100);
  }
});


onUnmounted(() => {
  if (carouselTrack.value) {
    carouselTrack.value.removeEventListener('scrollend', debouncedHandleScrollEnd);
    carouselTrack.value.removeEventListener('transitionend', debouncedHandleScrollEnd);
  }
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <div class="carousel-wrapper" ref="carouselWrapper">
    <div class="carousel-track" :class="{ 'alone-card': renderableSlides.length === 1 }" ref="carouselTrack">
      <component
        v-for="(slideVNode, index) in renderableSlides"
        :key="slideVNode.key ? `${slideVNode.key}-${index}` : `slide-${index}`"
        :is="slideVNode"
      />
    </div>

    <div v-if="renderableSlides.length > 1" class="carousel-navigation-buttons">
      <BaseSquaredButton @click="prevSlide" big left-arrow grayscale />
      <BaseSquaredButton @click="nextSlide" big right-arrow grayscale />
    </div>

    <div class="carousel-dots">
      <span
        v-for="(slide, index) in numOriginalSlidesArray"
        :key="index"
        :class="['dot', { active: publicCurrentIndex === index }]"
        @click="goToPublicSlide(index)"
      ></span>
    </div>
  </div>
</template>

<style scoped>
.carousel-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden; /* Обрезает содержимое, выходящее за границы обертки */
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.carousel-track {
  display: flex;
  flex-wrap: nowrap;
  gap: v-bind(gap + 'px');

  width: 100%; /* Заставляет трек переполняться */

  pointer-events: none;

  /* Для прокрутки и скрытия скроллбара */
  overflow-x: scroll; /* Разрешаем горизонтальную прокрутку */
  overflow-y: hidden; /* Скрываем вертикальную прокрутку */

  /* scroll-behavior: smooth; будет управляться через JS (carouselTrack.value.style.scrollBehavior) */
  -webkit-overflow-scrolling: touch; /* Улучшает прокрутку на iOS */

  /* Скрываем скроллбар */
  scrollbar-width: none; /* Для Firefox */
  -ms-overflow-style: none; /* Для Internet Explorer и Edge */

  mask-image: linear-gradient(to right,
  transparent 0%,  /* Полностью прозрачно на 0% ширины */
  black 25%,       /* Полностью видимо на 10% ширины */
  black 75%,       /* Остается полностью видимым до 90% ширины */
  transparent 100% /* Полностью прозрачно на 100% ширины */
  );

  &.alone-card {
    justify-content: center;
  }
}

/* Скрываем скроллбар для Webkit-браузеров */
.carousel-track::-webkit-scrollbar {
  display: none;
}

/* Стили для каждого дочернего элемента, переданного через слот */
.carousel-track > :deep(*) {
  flex-shrink: 0; /* Запрещаем слайдам сжиматься */
  width: v-bind(slideWidth + 'px'); /* Ширина слайда берется из props.slideWidth */
}

.carousel-navigation-buttons {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 16px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  margin-top: 15px; /* Отступ от кнопок */
  padding-bottom: 2px;
}

.dot {
  display: block;
  width: 10px;
  height: 10px;
  background-color: var(--element-bg);
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.dot.active {
  background-color: var(--accent-bg);
  transform: scale(1.2);
}
</style>